import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Check, MessageSquare } from 'lucide-react';
import { useCommunities } from '@/hooks/useCommunities';
import { useUserCommunities } from '@/hooks/useUserCommunities';
import { CommunityRequestForm } from '@/components/CommunityRequestForm';

interface CommunitySelectorProps {
  profileId: string;
}

export const CommunitySelector = ({ profileId }: CommunitySelectorProps) => {
  const { communities, loading: communitiesLoading } = useCommunities();
  const { userCommunities, joinCommunity, leaveCommunity, loading: userCommunitiesLoading } = useUserCommunities(profileId);
  const [showRequestForm, setShowRequestForm] = useState(false);

  const isJoined = (communityId: string) => {
    return userCommunities.some(uc => uc.community_id === communityId);
  };

  const handleCommunityToggle = async (communityId: string) => {
    if (isJoined(communityId)) {
      await leaveCommunity(communityId);
    } else {
      await joinCommunity(communityId);
    }
  };

  if (communitiesLoading || userCommunitiesLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Users className="h-5 w-5" />
            Communities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg animate-pulse">
                <div className="flex-1">
                  <div className="h-4 bg-muted-foreground/20 rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-muted-foreground/20 rounded w-1/2"></div>
                </div>
                <div className="h-8 w-20 bg-muted-foreground/20 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Users className="h-5 w-5" />
          Join Communities
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Connect with like-minded farmers and share knowledge
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {/* Login to Join Community Message */}
          <div className="mb-4 text-center py-2 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs sm:text-sm font-medium text-blue-800">
              🌱 Login to Join a Community - Connect & share with local farmers
            </p>
          </div>
          
          {communities
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((community) => {
            const joined = isJoined(community.id);
            return (
              <div
                key={community.id}
                className="flex items-center justify-between p-2 bg-card border rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-foreground text-sm">
                      {community.name}
                    </h4>
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {community.members_count}
                    </Badge>
                    {community.plant_hardiness_zone && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        Zone {community.plant_hardiness_zone}
                      </Badge>
                    )}
                  </div>
                  {community.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                      {community.description}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => handleCommunityToggle(community.id)}
                  variant={joined ? "default" : "outline"}
                  size="sm"
                  className="ml-2 flex-shrink-0 h-7 px-2 text-xs"
                >
                  {joined ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Joined
                    </>
                  ) : (
                    <>
                      <Plus className="h-3 w-3 mr-1" />
                      Join
                    </>
                  )}
                </Button>
              </div>
            );
          })}
          {communities.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No communities available yet</p>
            </div>
          )}
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-medium text-foreground">Request New Community</h5>
            <Button
              onClick={() => setShowRequestForm(!showRequestForm)}
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
            >
              <MessageSquare className="h-3 w-3 mr-1" />
              {showRequestForm ? 'Cancel' : 'Request'}
            </Button>
          </div>
          
          {showRequestForm && (
            <div className="bg-muted/30 p-3 rounded-md">
              <CommunityRequestForm onSuccess={() => setShowRequestForm(false)} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};